import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { FileText, Upload, Calendar, AlertTriangle } from 'lucide-react';
import type { EmployeeDocumentType } from '@/types/legacy';

const documentSchema = z.object({
  document_type: z.enum(['sick_leave', 'permission', 'justification', 'vacation_request', 'complaint', 'other']),
  title: z.string().min(3, 'El título debe tener al menos 3 caracteres'),
  description: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  is_urgent: z.boolean().default(false),
});

type DocumentFormData = z.infer<typeof documentSchema>;

interface EmployeeDocumentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const documentTypeLabels: Record<EmployeeDocumentType, string> = {
  sick_leave: 'Baja Médica',
  permission: 'Permiso',
  justification: 'Justificación',
  vacation_request: 'Solicitud de Vacaciones',
  complaint: 'Queja / Sugerencia',
  other: 'Otro',
};

const documentTypeIcons: Record<EmployeeDocumentType, string> = {
  sick_leave: '🏥',
  permission: '📋',
  justification: '📄',
  vacation_request: '🏖️',
  complaint: '💬',
  other: '📎',
};

export function EmployeeDocumentForm({ open, onOpenChange, onSuccess }: EmployeeDocumentFormProps) {
  const { user, profile } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<DocumentFormData>({
    resolver: zodResolver(documentSchema),
    defaultValues: {
      document_type: 'other',
      is_urgent: false,
    },
  });

  const documentType = watch('document_type');
  const isUrgent = watch('is_urgent');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validar tamaño (máximo 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'El archivo no puede superar los 10MB',
          variant: 'destructive',
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const onSubmit = async (data: DocumentFormData) => {
    if (!user) return;

    setLoading(true);
    try {
      // Por ahora guardamos sin archivo (la subida de archivos requiere storage)
      const documentData = {
        employee_id: user.id,
        employee_name: profile?.full_name || user.email,
        document_type: data.document_type,
        title: data.title,
        description: data.description || null,
        start_date: data.start_date || null,
        end_date: data.end_date || null,
        is_urgent: data.is_urgent,
        status: 'pending',
        file_name: selectedFile?.name || null,
        file_type: selectedFile?.type || null,
        file_size: selectedFile?.size || null,
      };

      const { error } = await api
        .from('employee_documents')
        .insert(documentData);

      if (error) throw error;

      toast({
        title: 'Documento enviado',
        description: 'Tu solicitud ha sido enviada a administración',
      });

      reset();
      setSelectedFile(null);
      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo enviar el documento',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const needsDates = ['sick_leave', 'permission', 'vacation_request'].includes(documentType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Enviar Documento a Administración
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Tipo de documento */}
          <div className="space-y-2">
            <Label>Tipo de Documento *</Label>
            <Select
              value={documentType}
              onValueChange={(value) => setValue('document_type', value as EmployeeDocumentType)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona el tipo" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(documentTypeLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    <span className="flex items-center gap-2">
                      <span>{documentTypeIcons[value as EmployeeDocumentType]}</span>
                      {label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Título */}
          <div className="space-y-2">
            <Label htmlFor="title">Asunto *</Label>
            <Input
              id="title"
              placeholder="Ej: Solicitud de permiso para cita médica"
              {...register('title')}
            />
            {errors.title && (
              <p className="text-sm text-destructive">{errors.title.message}</p>
            )}
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              placeholder="Detalles adicionales..."
              rows={3}
              {...register('description')}
            />
          </div>

          {/* Fechas (solo para ciertos tipos) */}
          {needsDates && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_date">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Fecha Inicio
                </Label>
                <Input
                  id="start_date"
                  type="date"
                  {...register('start_date')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end_date">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Fecha Fin
                </Label>
                <Input
                  id="end_date"
                  type="date"
                  {...register('end_date')}
                />
              </div>
            </div>
          )}

          {/* Archivo adjunto */}
          <div className="space-y-2">
            <Label>Archivo Adjunto</Label>
            <div className="border-2 border-dashed rounded-lg p-4 text-center">
              <input
                type="file"
                id="file-upload"
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileChange}
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center gap-2"
              >
                <Upload className="w-8 h-8 text-muted-foreground" />
                {selectedFile ? (
                  <span className="text-sm font-medium text-primary">
                    {selectedFile.name}
                  </span>
                ) : (
                  <span className="text-sm text-muted-foreground">
                    Haz clic para subir un archivo
                    <br />
                    <span className="text-xs">PDF, DOC, JPG, PNG (máx. 10MB)</span>
                  </span>
                )}
              </label>
            </div>
          </div>

          {/* Urgente */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-destructive/10 border border-destructive/20">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              <Label htmlFor="is_urgent" className="cursor-pointer">
                Marcar como urgente
              </Label>
            </div>
            <Switch
              id="is_urgent"
              checked={isUrgent}
              onCheckedChange={(checked) => setValue('is_urgent', checked)}
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-magma"
            >
              {loading ? 'Enviando...' : 'Enviar Documento'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
